// =========================================================================
// AZ Gruppe – dedicated service-page content (CMS-ready).
//
// Content is intentionally kept SEPARATE from layout so that every field below
// can later be sourced from a WordPress Headless CMS (titles, descriptions,
// hero media, galleries, videos, benefits, CTA labels and the service-specific
// form category).
//
// Placeholder tokens are clearly marked for later replacement:
//   [Text folgt]   – copy still to be written
//   [Bild folgt]   – image still to be uploaded
//   [Video folgt]  – video still to be added
// =========================================================================
import { services } from "@/lib/siteConfig";

export const PLACEHOLDER = {
  text: "[Text folgt]",
  image: "[Bild folgt]",
  video: "[Video folgt]",
};

const byId = Object.fromEntries(services.map((s) => [s.id, s]));

// Build a fully-shaped, CMS-ready content object for one service. Real, already
// approved copy (title + short intro + bullet labels) is reused; everything that
// still needs detailed copy/media uses the clearly-marked placeholders above.
const build = (id, extra = {}) => {
  const s = byId[id];
  // Pull the nested section overrides out; everything left (title, intro,
  // scope, …) is spread at the very end so it can override the defaults.
  const { hero, detail, media, benefits, cta, form, ...top } = extra;
  return {
    slug: id,
    // category MUST match ContactForm categories + backend ALLOWED_CATEGORIES
    category: id,
    icon: s.icon,
    eyebrow: "Leistungsbereich",
    title: s.title,
    intro: s.short,

    // A. Hero media (placeholder until real assets exist)
    hero: {
      image: PLACEHOLDER.image,
      video: PLACEHOLDER.video,
      ...(hero || {}),
    },

    // B. Detailed description
    detail: {
      heading: "Ausführliche Informationen folgen in Kürze",
      paragraphs: [PLACEHOLDER.text, PLACEHOLDER.text],
      ...(detail || {}),
    },

    // C. Media (gallery + video) – flexible, supports multiple images later
    media: {
      eyebrow: "Eindrücke",
      heading: "Bilder & Videos",
      subtitle: "Bildergalerie und Videos folgen in Kürze.",
      gallery: [PLACEHOLDER.image, PLACEHOLDER.image, PLACEHOLDER.image],
      video: PLACEHOLDER.video,
      ...(media || {}),
    },

    // D. Benefits – editable cards (bullet labels reused, copy still to follow)
    benefits: {
      eyebrow: "Ihre Vorteile",
      heading: `Warum ${s.title} mit der AZ Gruppe`,
      items: s.bullets.map((b) => ({ title: b, text: PLACEHOLDER.text })),
      ...(benefits || {}),
    },

    // E. Contact CTA labels
    cta: {
      eyebrow: "Kontakt",
      heading: "Sprechen Sie mit uns",
      text: "Wir beraten Sie persönlich und unverbindlich – wählen Sie einfach Ihren Kontaktweg.",
      ...(cta || {}),
    },

    // F. Service-specific form copy
    form: {
      eyebrow: "Direkt anfragen",
      heading: `Anfrage zu ${s.title}`,
      subtitle: "Schildern Sie uns Ihr Anliegen – wir melden uns zeitnah bei Ihnen.",
      ...(form || {}),
    },

    // Optional top-level overrides (title, intro, scope, …).
    ...top,
  };
};

export const serviceContent = {
  gutachten: build("gutachten", {
    title: "KFZ-Gutachten",
    intro:
      "Unabhängige Gutachten, klare Bewertung und professionelle Unterstützung im Schadensfall.",

    // Page-specific colour theme following the official AZ Gruppe Visual
    // Identity Guide (V1.0/2026): Gutachten = Navy + Red on a cool-white
    // background, with gold only as a thin highlight (~5%). Consumed by
    // ServicePageTemplate as CSS custom properties; the other service pages
    // keep the default palette.
    //   AZ Navy  #193D6D · AZ Red #7A1516 · Cool White #E7EDF5 · Gold #C99A3D
    theme: {
      "--svc-dark": "#193D6D", // AZ Navy — hero/CTA background, titles, lines
      "--svc-dark-rgb": "25,61,109",
      "--svc-ink": "#193D6D", // headings in navy
      "--svc-invert": "#F4F6F9", // near-white text on navy
      "--svc-primary": "#7A1516", // AZ Red — Gutachten accent (buttons, icons)
      "--svc-primary-hover": "#941d1e",
      "--svc-primary-soft": "rgba(122,21,22,0.09)",
      "--svc-accent": "#C99A3D", // Gold — highlights / eyebrow on dark
      "--svc-accent-glow": "rgba(201,154,61,0.14)",
      "--svc-page": "#F1F4F8", // cool white (leans blue, never warm)
      "--svc-surface": "#E7EDF5", // official Cool White as alternate band
      "--svc-border": "#D5DEE8",
      "--svc-body": "#2f3a49",
      "--svc-muted": "#586576",
      // Shift the shared `az-eyebrow` accent to AZ Red on light sections.
      "--az-burgundy": "359 71% 28%",
    },
    // Signature line-pattern palette: navy, red, gold, blue-grey (no neon/beige).
    lines: {
      dark: ["#93202a", "#5b7fb0", "#C99A3D", "#8296b3"],
      light: ["#7A1516", "#193D6D", "#C99A3D", "#6f86a8"],
    },

    // A. Hero – welcoming consultation image
    hero: {
      image: "/images/gutachten/gutachten-hero.png",
    },

    // B. Detailed description – "Ihr unabhängiger KFZ-Sachverständiger"
    detail: {
      heading: "Ihr unabhängiger KFZ-Sachverständiger",
      paragraphs: [
        "Die AZ Gruppe ist Ihr kompetenter Ansprechpartner für professionelle KFZ-Gutachten in Essen und Umgebung.",
        "Unsere IHK-geprüften KFZ-Sachverständigen erstellen unabhängige und fachgerechte Gutachten für Privatpersonen, Unternehmen und Autohäuser.",
        "Ob nach einem Verkehrsunfall, zur Fahrzeugbewertung oder zur Beweissicherung – wir stehen Ihnen mit Erfahrung, Fachwissen und einer objektiven Begutachtung zur Seite.",
        "Wir begleiten Sie während des gesamten Prozesses, erklären Ihnen jeden Schritt verständlich und unterstützen Sie bei der Schadenabwicklung.",
      ],
    },

    // C. Media – the three real Gutachten images (no video yet)
    media: {
      eyebrow: "Eindrücke",
      heading: "Einblicke in unsere Arbeit",
      subtitle:
        "Professionelle Begutachtung, persönliche Beratung und zuverlässige Schadenabwicklung.",
      gallery: [
        "/images/gutachten/gutachten-1.png",
        "/images/gutachten/gutachten-2.png",
        "/images/gutachten/gutachten-3.png",
      ],
      video: null,
    },

    // D. Benefits – "Ihre Vorteile auf einen Blick"
    benefits: {
      eyebrow: "Ihre Vorteile",
      heading: "Ihre Vorteile auf einen Blick",
      items: [
        {
          title: "IHK-geprüfte KFZ-Sachverständige",
          text: "Fachlich geprüfte Gutachter mit fundierter Erfahrung und anerkannter Qualifikation.",
        },
        {
          title: "Unabhängige Gutachtenerstellung",
          text: "Neutrale, objektive Bewertung – ausschließlich in Ihrem Interesse.",
        },
        {
          title: "Schnelle Terminvergabe",
          text: "Kurzfristige Termine – auf Wunsch auch vor Ort bei Ihnen oder am Fahrzeug.",
        },
        {
          title: "Persönliche Betreuung",
          text: "Ein fester Ansprechpartner begleitet Sie durch den gesamten Prozess.",
        },
        {
          title: "Unterstützung bei der Schadenabwicklung",
          text: "Wir helfen bei der Kommunikation mit Versicherung, Werkstatt und Anwalt.",
        },
        {
          title: "Für Privat- und Gewerbekunden",
          text: "Gutachten für Privatpersonen, Unternehmen und Autohäuser.",
        },
      ],
    },

    // Optional scope section – "Unser Rundum-Service – Unterstützung von A bis Z"
    scope: {
      eyebrow: "Rundum-Service",
      heading: "Unser Rundum-Service – Unterstützung von A bis Z",
      subtitle: "Ein Ansprechpartner. Klare Bewertung. Schnelle Bearbeitung.",
      items: [
        "Unfallgutachten & Beweissicherung",
        "Direkte Abwicklung mit der Versicherung",
        "Abschleppservice",
        "Ersatzfahrzeug",
        "Werkstatt-Partner",
        "Anwaltspartner für Verkehrsrecht",
        "Berücksichtigung von Nutzungsausfall, Wertminderung & Verdienstausfall",
        "Schnelle Terminvergabe – auch vor Ort",
        "Deutschlandweiter Service",
      ],
    },

    // E. Contact CTA – "Fahrzeugschaden oder Gutachten benötigt? Kein Problem."
    cta: {
      eyebrow: "Kontakt",
      heading: "Fahrzeugschaden oder Gutachten benötigt? Kein Problem.",
      text: "Wir erstellen professionelle KFZ-Gutachten schnell, unabhängig und zuverlässig. Ob Haftpflichtschaden, Fahrzeugbewertung oder Beweissicherung – wir begleiten Sie von der ersten Begutachtung bis zum fertigen Gutachten.",
      background: "/images/gutachten/gutachten-kontakt.png",
    },
  }),
  versicherung: build("versicherung", {
    // Insurance branch of the identity guide: Green + Blue. Navy stays the
    // structural colour; Insurance Green #156F39 replaces red as the accent,
    // gold remains the thin highlight.
    theme: {
      "--svc-dark": "#193D6D", // AZ Navy — hero/CTA background
      "--svc-dark-rgb": "25,61,109",
      "--svc-ink": "#193D6D", // navy headings
      "--svc-invert": "#F4F6F9",
      "--svc-primary": "#156F39", // Insurance Green — accent (buttons, icons)
      "--svc-primary-hover": "#1a8046",
      "--svc-primary-soft": "rgba(21,111,57,0.10)",
      "--svc-accent": "#C99A3D", // Gold — highlights / eyebrow on dark
      "--svc-accent-glow": "rgba(201,154,61,0.14)",
      "--svc-page": "#F1F4F8",
      "--svc-surface": "#E7EDF5",
      "--svc-border": "#D5DEE8",
      "--svc-body": "#2f3a49",
      "--svc-muted": "#586576",
      // Light-section eyebrows in Insurance Green.
      "--az-burgundy": "145 68% 26%",
    },
    lines: {
      dark: ["#1f7d47", "#5b7fb0", "#C99A3D", "#8296b3"],
      light: ["#156F39", "#193D6D", "#C99A3D", "#6e7683"],
    },

    // Exact copy from the supplied "Vwesicherung AZ.txt" – no additions.
    title: "Versicherung",
    intro: "Unabhängig. Persönlich. An Ihrer Seite.",

    hero: { image: "/images/versicherung/versicherung-2.png" },

    // Hero highlight strip.
    highlights: [
      "Über 750 Partner",
      "Deutschlandweit tätig",
      "Unabhängige Beratung",
      "Persönliche Betreuung",
    ],

    detail: {
      heading: "Ihr unabhängiger Partner in Deutschland",
      paragraphs: [
        "Wir vergleichen für Sie Angebote von über 750 Produkt- und Vertriebspartnern aus den Bereichen Versicherung, Finanzierung, Energie und Vorsorge.",
        "So erhalten Sie nicht die Lösung eines einzelnen Anbieters – sondern die Lösung, die wirklich zu Ihnen passt.",
      ],
    },

    media: {
      eyebrow: "Eindrücke",
      heading: "Persönliche Beratung, die zu Ihnen passt",
      subtitle: undefined,
      // All remaining brand images – the hero uses versicherung-2 (no repeats).
      gallery: [
        "/images/versicherung/versicherung-1.png",
        "/images/versicherung/versicherung-3.png",
        "/images/versicherung/versicherung-4.png",
        "/images/versicherung/versicherung-5.png",
        "/images/versicherung/versicherung-6.png",
      ],
      video: null,
    },

    // Three pillars: Unabhängig · Persönlich · Übersichtlich.
    benefits: {
      eyebrow: "",
      heading: "Unabhängig. Persönlich. Übersichtlich.",
      items: [
        {
          title: "Unabhängig",
          text: "Wir arbeiten nicht für eine Versicherung – wir arbeiten für unsere Kunden. Über 750 Partner im Vergleich.",
        },
        {
          title: "Persönlich",
          text: "Ein fester Ansprechpartner für alle Themen und Fragen. Wir sind für Sie da.",
        },
        {
          title: "Übersichtlich",
          text: "Alle Ihre Verträge im Blick. Mehr Transparenz, mehr Kontrolle, mehr Sicherheit.",
        },
      ],
    },

    // Bestandskunden section.
    scope: {
      eyebrow: "",
      heading: "Bereits Verträge vorhanden? Kein Problem.",
      subtitle:
        "Egal bei welcher Versicherung, Bank oder welchem Anbieter Sie aktuell Kunde sind. Wir können Ihre bestehenden Verträge übernehmen und Ihr zentraler Ansprechpartner werden.",
      items: [
        "Verträge bündeln und Überblick schaffen",
        "Optimierungspotenziale erkennen",
        "Persönliche Betreuung und Beratung",
        "Unterstützung im Schadensfall",
      ],
    },

    // 4-step process.
    steps: {
      eyebrow: "So einfach funktioniert es",
      heading: "In 4 Schritten zu mehr Sicherheit und Ersparnis",
      items: [
        {
          title: "Kostenloses Gespräch",
          text: "Wir lernen Ihre Situation kennen und verstehen Ihre Wünsche und Ziele.",
        },
        {
          title: "Analyse",
          text: "Wir prüfen Ihre bestehenden Verträge und analysieren Ihre Absicherung.",
        },
        {
          title: "Vergleich",
          text: "Wir vergleichen passende Angebote aus über 750 Partnern für Sie.",
        },
        {
          title: "Betreuung",
          text: "Wir begleiten Sie langfristig und sind im Schadensfall an Ihrer Seite.",
        },
      ],
    },

    cta: {
      eyebrow: "AZ Gruppe",
      heading: "Ihr Partner von A bis Z",
      text: "Wir begleiten Privatpersonen und Unternehmen bei Versicherungen, Finanzierungen, Energie und Vorsorge. Transparent. Persönlich. Unabhängig.",
      background: "/images/versicherung/versicherung-kontakt.png",
      points: [
        { title: "Sitz in Essen", text: "Deutschlandweit für Sie tätig." },
        { title: "Über 750 Partner", text: "Zugriff auf ein starkes Netzwerk." },
        { title: "Ein Ansprechpartner", text: "Für alle Themen rund um Ihre Verträge." },
        { title: "Schadenservice", text: "Wir lassen Sie im Ernstfall nicht allein." },
      ],
    },
  }),

  "kfz-zulassung": build("kfz-zulassung", {
    hero: { image: "/images/zulassung/zulassung-1.png" },

    detail: {
      heading: "Ihre Fahrzeugzulassung – schnell & unkompliziert",
      paragraphs: [
        "Die AZ Gruppe übernimmt die komplette Fahrzeugzulassung für Sie – An-, Um- und Abmeldung inklusive. Sie sparen sich Wartezeiten, Behördengänge und Papierkram.",
        "Wir bereiten alle notwendigen Dokumente vor, prägen Kennzeichen und wickeln sämtliche Formalitäten zuverlässig ab – für Privatpersonen, Autohäuser und Unternehmen.",
        "Auf Wunsch erledigen wir die Zulassung deutschlandweit und digital, damit Ihr Fahrzeug schnellstmöglich einsatzbereit ist.",
      ],
    },

    media: {
      eyebrow: "Eindrücke",
      heading: "Alles rund um Ihre Zulassung",
      subtitle: "Kennzeichen, Dokumente und Formalitäten – zuverlässig aus einer Hand.",
      gallery: [
        "/images/zulassung/zulassung-1.png",
        "/images/zulassung/zulassung-2.png",
        "/images/zulassung/zulassung-3.png",
      ],
      video: null,
    },

    benefits: {
      eyebrow: "Ihre Vorteile",
      heading: "Ihre Vorteile auf einen Blick",
      items: [
        {
          title: "An-, Um- und Abmeldung",
          text: "Wir übernehmen jede Art der Zulassung – ob Neuwagen, Gebrauchtwagen oder Halterwechsel.",
        },
        {
          title: "Alle Formalitäten inklusive",
          text: "Von der eVB-Nummer bis zur Feinstaubplakette kümmern wir uns um jeden Schritt.",
        },
        {
          title: "Dokumente vorbereitet",
          text: "Wir stellen alle erforderlichen Unterlagen zusammen und prüfen sie auf Vollständigkeit.",
        },
        {
          title: "Kennzeichenprägung",
          text: "Ihre Kennzeichen erhalten Sie direkt bei uns – ohne zusätzlichen Weg.",
        },
        {
          title: "Schnelle Abwicklung",
          text: "Kurze Bearbeitungszeiten, damit Ihr Fahrzeug ohne Verzögerung angemeldet ist.",
        },
        {
          title: "Deutschlandweiter Service",
          text: "Auf Wunsch übernehmen wir die Zulassung bundesweit und digital.",
        },
      ],
    },

    cta: {
      eyebrow: "Kontakt",
      heading: "Fahrzeug anmelden oder ummelden? Kein Problem.",
      text: "Wir übernehmen die komplette Zulassung inklusive aller Dokumente und Kennzeichen – schnell, zuverlässig und ohne Papierkram für Sie.",
      background: "/images/zulassung/zulassung-kontakt.png",
    },
  }),

  services: build("services", {
    hero: { image: "/images/services/services-hero.png" },

    detail: {
      heading: "Verträge & Services – clever gebündelt",
      paragraphs: [
        "Die AZ Gruppe begleitet Sie bei allen alltäglichen Vertrags- und Serviceangelegenheiten – von Strom- und Gasverträgen bis zur Optimierung bestehender Verträge.",
        "Wir vergleichen Anbieter, decken Sparpotenziale auf und übernehmen den Wechsel für Sie. So sparen Sie Zeit, Geld und Aufwand – ohne sich um Details kümmern zu müssen.",
        "Als langfristiger Partner behalten wir Ihre Verträge im Blick und melden uns, sobald sich eine bessere Lösung ergibt.",
      ],
    },

    // No dedicated imagery yet → media section is automatically hidden.
    media: { gallery: [], video: null },

    benefits: {
      eyebrow: "Ihre Vorteile",
      heading: "Ihre Vorteile auf einen Blick",
      items: [
        {
          title: "Strom- und Gasverträge",
          text: "Wir finden den passenden Tarif und übernehmen den Wechsel vollständig für Sie.",
        },
        {
          title: "Vertragsoptimierung",
          text: "Bestehende Verträge werden regelmäßig geprüft und bei Bedarf verbessert.",
        },
        {
          title: "Vertrags- & Servicebetreuung",
          text: "Ein Ansprechpartner für Ihre laufenden Verträge im Alltag.",
        },
        {
          title: "Sparpotenziale aufgedeckt",
          text: "Wir vergleichen Anbieter und zeigen Ihnen transparent, wo Sie sparen.",
        },
        {
          title: "Langfristige Begleitung",
          text: "Wir bleiben an Ihrer Seite und optimieren Ihre Verträge fortlaufend.",
        },
        {
          title: "Persönlich & zuverlässig",
          text: "Feste Ansprechpartner und verständliche Beratung ohne Warteschleife.",
        },
      ],
    },

    cta: {
      eyebrow: "Kontakt",
      heading: "Verträge optimieren oder wechseln? Kein Problem.",
      text: "Wir vergleichen Anbieter, decken Sparpotenziale auf und übernehmen den Wechsel – persönlich, transparent und ganz ohne Aufwand für Sie.",
      background: "/images/services/services-kontakt.png",
    },
  }),
};

export const getServiceContent = (slug) => serviceContent[slug];

// Convenience list for navigation menus (desktop dropdown + mobile sub-links).
export const serviceLinks = services.map((s) => ({
  to: `/${s.id}`,
  id: s.id,
  label: s.title,
  icon: s.icon,
}));
